import React, { useCallback, useEffect, useMemo } from 'react';
import SidebarNew from './SidebarNew';
import HeaderNew from './HeaderNew';
import MobileTabBar from './MobileTabBar';
import FooterNew from './FooterNew';
import CookieBanner from './CookieBanner';
import { Outlet, useLocation } from 'react-router-dom';
import { ToastProvider } from '../shared/ToastContainer';
import { useUserStore } from '../../stores/user-store';

const DEFAULT_SIDEBAR_WIDTH = 240;

// 防抖函数
function useDebouncedCallback<T extends (...args: any[]) => any>(
  callback: T,
  delay: number
): T {
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  
  return useCallback((...args: Parameters<T>) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]) as T;
}

const LayoutNew: React.FC = () => {
  const { preferences, updatePreferences } = useUserStore();
  
  // 使用本地状态立即响应，同时同步到 store
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(() => {
    // 优先从 localStorage 读取（避免闪烁），store 加载后会同步
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved === 'true';
  });
  const [sidebarWidth, setSidebarWidth] = React.useState(() => {
    const saved = localStorage.getItem('sidebar-width');
    return saved ? parseInt(saved, 10) : DEFAULT_SIDEBAR_WIDTH;
  });
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = React.useState(false);
  const location = useLocation();
  
  // 在文件详情页面隐藏页脚
  const isFileDetailsPage = location.pathname.includes('/files/');

  // 当用户偏好从数据库加载后，同步到本地状态
  useEffect(() => {
    if (preferences.sidebar_collapsed !== undefined) {
      setSidebarCollapsed(preferences.sidebar_collapsed);
    }
    if (preferences.sidebar_width !== undefined) {
      setSidebarWidth(preferences.sidebar_width);
    }
  }, [preferences.sidebar_collapsed, preferences.sidebar_width]);

  // 防抖保存偏好到数据库（500ms 延迟）
  const debouncedSavePreferences = useDebouncedCallback(
    (updates: { sidebar_width?: number; sidebar_collapsed?: boolean }) => {
      updatePreferences(updates);
    },
    500
  );

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((v) => {
      const newValue = !v;
      // 同时保存到 localStorage（即时响应）和数据库（防抖）
      localStorage.setItem('sidebar-collapsed', String(newValue));
      debouncedSavePreferences({ sidebar_collapsed: newValue });
      return newValue;
    });
  }, [debouncedSavePreferences]);

  const handleWidthChange = useCallback((width: number) => {
    setSidebarWidth(width);
    // 同时保存到 localStorage（即时响应）和数据库（防抖）
    localStorage.setItem('sidebar-width', String(width));
    debouncedSavePreferences({ sidebar_width: width });
  }, [debouncedSavePreferences]);

  const toggleMobileSidebar = () => {
    setIsMobileSidebarOpen((v) => !v);
  };

  // 键盘快捷键: ⌘+B 或 Ctrl+B 切换侧边栏
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault();
        toggleSidebar();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleSidebar]);

  useEffect(() => {
    if (isMobileSidebarOpen) {
      const { overflow } = document.body.style as { overflow: string };
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = overflow || '';
      };
    }
  }, [isMobileSidebarOpen]);

  return (
    <ToastProvider>
      <div className="flex h-[100dvh] overflow-hidden bg-slate-50">
        <SidebarNew 
          collapsed={sidebarCollapsed}
          onToggleCollapse={toggleSidebar}
          width={sidebarWidth}
          onWidthChange={handleWidthChange}
          mobileOpen={isMobileSidebarOpen}
          onCloseMobile={() => setIsMobileSidebarOpen(false)}
        />

        {isMobileSidebarOpen && (
          <div
            className="fixed inset-0 z-30 bg-black/40 md:hidden transition-opacity duration-300"
            onClick={() => setIsMobileSidebarOpen(false)}
          />
        )}

        <main className="flex-1 flex flex-col h-full overflow-hidden bg-slate-50">
          <HeaderNew
            toggleSidebar={toggleSidebar}
            sidebarCollapsed={sidebarCollapsed}
            toggleMobileSidebar={toggleMobileSidebar}
            mobileSidebarOpen={isMobileSidebarOpen}
          />
          <div className="flex-1 overflow-y-auto min-h-0 flex flex-col relative scroll-smooth custom-scrollbar p-6 md:p-8">
            <div className="flex-1 w-full mx-auto" style={{ maxWidth: '1400px' }}>
              <Outlet />
            </div>
            {!isFileDetailsPage && <FooterNew />}
          </div>
          <MobileTabBar />
        </main>

        <CookieBanner />
      </div>
    </ToastProvider>
  );
};

export default LayoutNew;
